// The block at the top of a page: where you are, what the page is, and what you can do here.
import { I80Element, define, h } from "../base.js";
import { icon } from "../icons.js";

const TITLE_TAGS = ["h1", "h2", "h3", "h4", "h5", "h6"];
const TILE_COLORS = ["neutral", "brand", "coral", "pink", "green", "sky", "purple", "blue", "yellow", "teal"];

/** Our own part inside `parent`, never one belonging to a nested <i80-*> child. */
const own = (parent, name) => parent.querySelector(`:scope > [data-i80-part="${name}"]`);

/**
 * <i80-page-header title="Cluster" subtitle="eu-west-1" description="…" icon="server-cluster" icon-color="pink">
 *   <i80-breadcrumb slot="breadcrumb">…</i80-breadcrumb>
 *   <i80-badge slot="badges" color="success">Running</i80-badge>
 *   <i80-button slot="actions" color="primary" icon="plus">Create</i80-button>
 *   <div>Anything else goes under the title, as metadata.</div>
 * </i80-page-header>
 *
 * Title, subtitle and description are attributes because they are single lines of text in
 * fixed typography; everything else is a child, so the breadcrumb, the badges, the actions
 * and your own metadata are the components (or the plain HTML) you already have.
 */
export class I80PageHeader extends I80Element {
  static get observedAttributes() {
    return ["title", "title-tag", "subtitle", "description", "icon", "icon-color"];
  }

  setup() {
    let root = this.part("page-header");
    if (!root) {
      root = h("header", { class: "i80-page-header", "data-i80-part": "page-header" },
        h("div", { class: "i80-page-header__body", "data-i80-part": "body" },
          h("div", { class: "i80-page-header__main", "data-i80-part": "main" },
            h("div", { class: "i80-page-header__content", "data-i80-part": "content" },
              h("div", { class: "i80-page-header__title-wrapper", "data-i80-part": "title-wrapper" }),
              h("div", { class: "i80-page-header__metadata", "data-i80-part": "metadata" })))));
      this.append(root);
      // The children are whole components — a breadcrumb, a dropdown — and when a tree that
      // was built off-document is inserted, they exist but have not built themselves yet.
      // Moving one then would move a half-finished component, so we build the frame now and
      // take the children in a microtask later, the same route children arriving late take.
      queueMicrotask(() => {
        if (!this.hasStrayContent()) return;
        this.adopt();
        this.update();
      });
    }
    this._root = root;
    this._body = own(root, "body");
    this._main = own(this._body, "main");
    const content = own(this._main, "content");
    this._titles = own(content, "title-wrapper");
    this._metadata = own(content, "metadata");
  }

  /** The badges and the actions have a box of their own only when there is something in it. */
  _badges() {
    return own(this._titles, "badges")
      ?? this._titles.appendChild(h("div", { class: "i80-page-header__badges-wrapper", "data-i80-part": "badges" }));
  }

  _actions() {
    return own(this._main, "actions")
      ?? this._main.appendChild(h("div", { class: "i80-page-header__actions", "data-i80-part": "actions" }));
  }

  adopt() {
    for (const node of this.takeContent()) {
      const slot = node.nodeType === Node.ELEMENT_NODE ? node.getAttribute("slot") : null;
      if (slot === "breadcrumb") this._root.prepend(node);
      else if (slot === "badges") this._badges().append(node);
      else if (slot === "actions") this._actions().append(node);
      else this._metadata.append(node);
    }
  }

  update() {
    own(this._titles, "title")?.remove();
    const title = this.getAttribute("title");
    // No title, no heading: an empty one would be announced as a heading with nothing in it.
    if (title) {
      this._titles.prepend(h(this.choice("title-tag", TITLE_TAGS, "h1"), {
        class: "i80-page-header__title i80-text i80-typography-display-500 i80-font-weight-bold i80-foreground-strong",
        "data-i80-part": "title",
      }, title));
    }

    const line = (name, color) => {
      own(this._metadata, name)?.remove();
      const value = this.getAttribute(name);
      return value && h("p", {
        class: `i80-page-header__${name} i80-text i80-typography-body-200 i80-font-weight-regular i80-foreground-${color}`,
        "data-i80-part": name,
      }, value);
    };
    this._metadata.prepend(...[line("subtitle", "faint"), line("description", "primary")].filter(Boolean));

    own(this._body, "icon-tile")?.remove();
    const name = this.getAttribute("icon");
    if (name) {
      this._body.prepend(h("div", {
        class: `i80-icon-tile i80-icon-tile--icon i80-icon-tile--size-medium i80-icon-tile--color-${this.choice("icon-color", TILE_COLORS, "neutral")}`,
        "aria-hidden": "true", "data-i80-part": "icon-tile",
      }, h("div", { class: "i80-icon-tile__icon" }, icon(name, { size: "24", stretched: true }))));
    }
  }
}

define("i80-page-header", I80PageHeader);
